import React, { useState, useRef, useEffect, DragEvent, ChangeEvent } from 'react'
import { createPortal } from 'react-dom'
import { noop } from 'lodash-es'
import classnames from 'classnames'
import FileInput from './FileInput'
import HorizontalSpinner from '../HorizontalSpinner'

type DropzoneProps = {
  onChange?: (files: File[]) => void
  showProgress?: boolean
  maxItems?: number
  accept?: string // Allowed file types (e.g., "image/*, .pdf, .csv")
  instructionsText?: string
}

const Dropzone: React.FC<DropzoneProps> = props => {
  const {
    onChange = noop,
    showProgress = false,
    maxItems = Infinity,
    accept = '', // Default to accepting all files
    instructionsText = 'Drag & drop files here, or click to upload.',
  } = props

  const [draggingOver, setDraggingOver] = useState(false)
  const portalElRef = useRef(document.createElement('div'))
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    document.body.appendChild(portalElRef.current)
    return () => {
      portalElRef.current.remove()
    }
  }, [])

  const fileInputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return
    onChange(Array.from(event.target.files))
  }

  const browseForFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    } else {
      throw new Error('File input not found')
    }
  }

  const fileMatchesAccept = (file: File) => {
    if (!accept) return true // No restriction
    const acceptedTypes = accept.split(',').map(type => type.trim())
    return acceptedTypes.some(type =>
      type.startsWith('.')
        ? file.name.toLowerCase().endsWith(type)
        : file.type === type || file.type.startsWith(type.replace('/*', ''))
    )
  }

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    const files: File[] = []
    if (event.dataTransfer.items) {
      Array.from(event.dataTransfer.items).forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile()
          if (file && fileMatchesAccept(file)) files.push(file)
        }
      })
    } else {
      Array.from(event.dataTransfer.files).forEach(file => {
        if (fileMatchesAccept(file)) {
          files.push(file)
        }
      })
    }
    if (files.length > 0) onChange(files.slice(0, maxItems))
  }

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setDraggingOver(true)
  }

  const handleDragLeave = () => {
    setDraggingOver(false)
  }

  const getLoadingContent = () => {
    if (!showProgress) return null
    return (
      <div className="progress-container">
        <HorizontalSpinner />
      </div>
    )
  }

  const contentClassNames = classnames('dropzone__content', {
    'dropzone__content--dragging-over': draggingOver,
  })

  return (
    <>
      <div className="dropzone">
        <div
          onClick={browseForFile}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={contentClassNames}
        >
          <div className="dropzone__instructions">{instructionsText}</div>
        </div>
        {getLoadingContent()}
      </div>
      {createPortal(
        <div className="hidden">
          <FileInput ref={fileInputRef} onChange={fileInputChangeHandler} />
        </div>,
        portalElRef.current
      )}
    </>
  )
}

export default Dropzone
